import React, {
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { Animated, Pressable, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';
import {
  dismissOperationFeedback,
  getOperationFeedbackSnapshot,
  registerOperationFeedbackHost,
  subscribeOperationFeedback,
} from '../utils/operationFeedback';
import { webPointer } from '../utils/webLayout';

/**
 * Renders add/update/delete popups raised through `runOperation`.
 * Mounted once at the app root; dialogs that use their own `Modal` mount one
 * inside it too, so the popup is never hidden behind an open dialog.
 */
export function OperationFeedbackHost() {
  const palette = useAppColors();
  const insets = useSafeAreaInsets();
  const [hostId, setHostId] = useState<number | null>(null);
  const { feedback, activeHostId } = useSyncExternalStore(
    subscribeOperationFeedback,
    getOperationFeedbackSnapshot,
    getOperationFeedbackSnapshot,
  );
  const offset = useRef(new Animated.Value(-16)).current;
  const isActiveHost = hostId !== null && hostId === activeHostId;
  const visible = isActiveHost ? feedback : null;

  useEffect(() => {
    const registration = registerOperationFeedbackHost();
    setHostId(registration.hostId);
    return registration.unregister;
  }, []);

  useEffect(() => {
    if (!visible) {
      offset.setValue(-16);
      return;
    }
    Animated.timing(offset, {
      toValue: 0,
      duration: 180,
      useNativeDriver: false,
    }).start();
  }, [visible, offset]);

  if (!visible) return null;

  const success = visible.outcome === 'success';
  const accent = success ? palette.success : palette.error;

  return (
    <View
      pointerEvents="box-none"
      style={[styles.overlay, { top: insets.top + spacing.md }]}
    >
      <Animated.View style={{ transform: [{ translateY: offset }] }}>
        <Pressable
          testID="operation-feedback-popup"
          accessibilityRole="alert"
          accessibilityLiveRegion="polite"
          accessibilityHint="Dismisses this message"
          onPress={dismissOperationFeedback}
          style={[
            styles.card,
            webPointer,
            {
              backgroundColor: palette.surface,
              borderColor: palette.border,
              borderLeftColor: accent,
              shadowColor: palette.black,
            },
          ]}
        >
          <View style={[styles.badge, { backgroundColor: accent }]}>
            <Text style={[styles.badgeText, { color: success ? palette.onSuccess : palette.onError }]}>
              {success ? '✓' : '!'}
            </Text>
          </View>
          <View style={styles.copy}>
            <Text style={[styles.title, { color: palette.textPrimary }]}>
              {visible.title}
            </Text>
            {visible.detail ? (
              <Text
                style={[styles.detail, { color: palette.textSecondary }]}
                numberOfLines={3}
              >
                {visible.detail}
              </Text>
            ) : null}
          </View>
        </Pressable>
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    left: spacing.lg,
    right: spacing.lg,
    alignItems: 'center',
    zIndex: 1000,
    elevation: 1000,
  },
  card: {
    width: '100%',
    maxWidth: 420,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    borderLeftWidth: 4,
    shadowOpacity: 0.15,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 4 },
    elevation: 6,
  },
  badge: {
    width: 26,
    height: 26,
    borderRadius: 13,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeText: {
    ...typography.button,
    fontSize: 15,
    lineHeight: 18,
  },
  copy: { flex: 1 },
  title: typography.button,
  detail: { ...typography.caption, marginTop: 2 },
});
